import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Loader as Loader2, CircleCheck as CheckCircle2, Upload, Search, Mountain, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { parseSkiAreasCSV, parseRunsCSV, parseLiftsCSV } from '../lib/csvParser';

const BATCH_SIZE = 100;

export default function BulkLoadResorts() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [resorts, setResorts] = useState([]);
  const [filteredResorts, setFilteredResorts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResorts, setSelectedResorts] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState('select'); // 'csv', 'select', 'publish'
  const [publishProgress, setPublishProgress] = useState([]);
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    if (profile && !profile.is_admin) {
      navigate('/settings');
      return;
    }

    loadOpenSkiDataResorts();
  }, [profile, navigate]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredResorts(resorts);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const results = resorts.filter(r => {
      const name = r.name?.toLowerCase() || '';
      const regions = r.regions?.toLowerCase() || '';
      const countries = r.countries?.toLowerCase() || '';
      return name.includes(query) || regions.includes(query) || countries.includes(query);
    });
    
    setFilteredResorts(results);
  }, [resorts, searchQuery]);

  const loadOpenSkiDataResorts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('ski_areas_openskidata')
        .select('*')
        .order('name')
        .limit(10000); // Set high limit to load all ~5700 resorts

      if (error) throw error;
      setResorts(data || []);
      setFilteredResorts(data || []);
    } catch (err) {
      console.error('Error loading OpenSkiData resorts:', err);
      toast.error('Failed to load OpenSkiData resorts');
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUpload = async (event, fileType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(`Reading ${fileType} file...`);

    try {
      const text = await file.text();
      let records = [];
      let filteredCount = 0;

      if (fileType === 'ski_areas') {
        // For ski_areas, track how many records were filtered
        const allRecords = parseSkiAreasCSV(text);
        records = allRecords;
        // Calculate filtered count from CSV (total lines - 1 header - actual records)
        const csvLines = text.trim().split('\n');
        filteredCount = Math.max(0, (csvLines.length - 1) - records.length);
      } else if (fileType === 'runs') {
        records = parseRunsCSV(text);
      } else if (fileType === 'lifts') {
        records = parseLiftsCSV(text);
      }

      if (records.length === 0) {
        if (fileType === 'ski_areas' && filteredCount > 0) {
          toast.error(`No qualifying ski areas found. ${filteredCount} records filtered (only operating ski areas with downhill are imported)`);
        } else {
          toast.error(`No valid records found in ${file.name}`);
        }
        setUploading(false);
        return;
      }

      let successMessage = `Uploading ${records.length} ${fileType} records`;
      if (fileType === 'ski_areas' && filteredCount > 0) {
        successMessage += ` (${filteredCount} filtered - only operating with downhill)`;
      }
      setUploadProgress(successMessage);

      // Upload in batches
      for (let i = 0; i < records.length; i += BATCH_SIZE) {
        const batch = records.slice(i, i + BATCH_SIZE);
        setUploadProgress(`Uploading ${fileType}: ${Math.min(i + BATCH_SIZE, records.length)}/${records.length}`);

        if (fileType === 'ski_areas') {
          // For ski_areas, use upsert to handle updates
          const { error } = await supabase
            .from('ski_areas_openskidata')
            .upsert(batch, { onConflict: 'openskidata_id' });

          if (error) throw error;
        } else if (fileType === 'runs') {
          // For runs, map ski_area_names to ski_area_openskidata_id and filter
          let validRuns = [];
          for (const run of batch) {
            if (run.ski_area_names) {
              const { data: skiArea } = await supabase
                .from('ski_areas_openskidata')
                .select('id')
                .eq('name', run.ski_area_names)
                .single();

              if (skiArea) {
                run.ski_area_openskidata_id = skiArea.id;
                validRuns.push(run);
              }
              // Skip runs for ski areas that weren't imported
            }
          }

          if (validRuns.length > 0) {
            const { error } = await supabase
              .from('runs_openskidata')
              .upsert(validRuns, { onConflict: 'openskidata_id,ski_area_openskidata_id' });

            if (error) throw error;
          }
        } else if (fileType === 'lifts') {
          // For lifts, map ski_area_names to ski_area_openskidata_id and filter
          let validLifts = [];
          for (const lift of batch) {
            if (lift.ski_area_names) {
              const { data: skiArea } = await supabase
                .from('ski_areas_openskidata')
                .select('id')
                .eq('name', lift.ski_area_names)
                .single();

              if (skiArea) {
                lift.ski_area_openskidata_id = skiArea.id;
                validLifts.push(lift);
              }
              // Skip lifts for ski areas that weren't imported
            }
          }

          if (validLifts.length > 0) {
            const { error } = await supabase
              .from('lifts_openskidata')
              .upsert(validLifts, { onConflict: 'openskidata_id,ski_area_openskidata_id' });

            if (error) throw error;
          }
        }
      }

      let toastMessage = `✓ Uploaded ${records.length} ${fileType}`;
      if (fileType === 'ski_areas' && filteredCount > 0) {
        toastMessage += ` (${filteredCount} filtered)`;
      }
      toast.success(toastMessage);
      await loadOpenSkiDataResorts();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(`Failed to upload ${fileType}: ${err.message}`);
    } finally {
      setUploading(false);
      setUploadProgress('');
      event.target.value = '';
    }
  };

  const toggleResort = (id) => {
    const newSelected = new Set(selectedResorts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedResorts(newSelected);
  };

  const selectAll = () => {
    setSelectedResorts(new Set(filteredResorts.map(r => r.id)));
  };

  const deselectAll = () => {
    setSelectedResorts(new Set());
  };

  const publishSelectedResorts = async () => {
    if (selectedResorts.size === 0) {
      toast.error('No resorts selected');
      return;
    }

    setIsPublishing(true);
    setCurrentStep('publish');
    setPublishProgress([]);

    const resortsToPublish = Array.from(selectedResorts)
      .map(id => resorts.find(r => r.id === id))
      .filter(Boolean);

    for (let i = 0; i < resortsToPublish.length; i++) {
      const resort = resortsToPublish[i];
      setPublishProgress(prev => [...prev, { name: resort.name, status: 'publishing' }]);

      try {
        // 1. Check if ski area already exists in main table by openskidata_id
        const { data: existingSkiArea } = await supabase
          .from('ski_areas')
          .select('id')
          .eq('osm_id', resort.openskidata_id)
          .single();

        let skiAreaId;
        if (existingSkiArea) {
          // Update existing
          skiAreaId = existingSkiArea.id;
          await supabase
            .from('ski_areas')
            .update({
              name: resort.name,
              country: resort.countries?.split(',')[0]?.trim(),
              region: resort.regions?.split(',')[0]?.trim(),
              vertical_m: resort.vertical_m,
              elevation_base_m: resort.min_elevation_m,
              elevation_summit_m: resort.max_elevation_m,
              website: resort.websites,
              last_synced_at: new Date().toISOString()
            })
            .eq('id', skiAreaId);
        } else {
          // Create new
          const { data: newSkiArea, error: skiAreaError } = await supabase
            .from('ski_areas')
            .insert([{
              name: resort.name,
              osm_id: resort.openskidata_id,
              country: resort.countries?.split(',')[0]?.trim(),
              region: resort.regions?.split(',')[0]?.trim(),
              vertical_m: resort.vertical_m,
              elevation_base_m: resort.min_elevation_m,
              elevation_summit_m: resort.max_elevation_m,
              website: resort.websites,
              source: 'openskidata',
              is_published: true,
              is_active: true,
              load_status: 'loaded',
              last_synced_at: new Date().toISOString()
            }])
            .select()
            .single();

          if (skiAreaError) throw skiAreaError;
          skiAreaId = newSkiArea.id;
        }

        // 2. Copy runs from openskidata to main table
        const { data: openSkiRuns } = await supabase
          .from('runs_openskidata')
          .select('*')
          .eq('ski_area_openskidata_id', resort.id);

        if (openSkiRuns && openSkiRuns.length > 0) {
          const runsToInsert = openSkiRuns.map(run => ({
            ski_area_id: skiAreaId,
            name: run.name,
            difficulty: run.difficulty || run.piste_type,
            piste_difficulty: run.difficulty,
            piste_type: run.piste_type,
            ref: run.ref,
            description: run.description,
            vertical_ft: run.vertical_m ? Math.round(run.vertical_m * 3.28084) : null,
            length_m: run.inclined_length_m,
            geom: run.geometry
          }));

          // Delete existing runs for this ski area first
          await supabase
            .from('runs')
            .delete()
            .eq('ski_area_id', skiAreaId);

          // Insert new runs
          const { error: runsError } = await supabase
            .from('runs')
            .insert(runsToInsert);

          if (runsError) throw runsError;
        }

        // 3. Copy lifts from openskidata to main table
        const { data: openSkiLifts } = await supabase
          .from('lifts_openskidata')
          .select('*')
          .eq('ski_area_openskidata_id', resort.id);

        if (openSkiLifts && openSkiLifts.length > 0) {
          const liftsToInsert = openSkiLifts.map(lift => ({
            ski_area_id: skiAreaId,
            name: lift.name,
            lift_type: lift.lift_type,
            aerialway: lift.lift_type,
            capacity: lift.capacity,
            ref: lift.ref,
            geom: lift.geometry
          }));

          // Delete existing lifts for this ski area first
          await supabase
            .from('lifts')
            .delete()
            .eq('ski_area_id', skiAreaId);

          // Insert new lifts
          const { error: liftsError } = await supabase
            .from('lifts')
            .insert(liftsToInsert);

          if (liftsError) throw liftsError;
        }

        // 4. Update run and lift counts
        const { count: runCount } = await supabase
          .from('runs')
          .select('*', { count: 'exact', head: true })
          .eq('ski_area_id', skiAreaId);

        const { count: liftCount } = await supabase
          .from('lifts')
          .select('*', { count: 'exact', head: true })
          .eq('ski_area_id', skiAreaId);

        await supabase
          .from('ski_areas')
          .update({
            run_count: runCount || 0,
            lift_count: liftCount || 0,
            is_published: true
          })
          .eq('id', skiAreaId);

        // 5. Mark as linked in openskidata table
        await supabase
          .from('ski_areas_openskidata')
          .update({ linked_ski_area_id: skiAreaId, is_published: true })
          .eq('id', resort.id);

        setPublishProgress(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { name: resort.name, status: 'success' };
          return updated;
        });
      } catch (err) {
        console.error(`Error publishing ${resort.name}:`, err);
        setPublishProgress(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { name: resort.name, status: 'error', error: err.message };
          return updated;
        });
      }
    }

    setIsPublishing(false);
    const successCount = publishProgress.filter(p => p.status === 'success').length;
    toast.success(`Published ${successCount} of ${resortsToPublish.length} resorts`);

    await loadOpenSkiDataResorts();
    setSelectedResorts(new Set());
  };

  if (!profile?.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-4">
      <div className="max-w-6xl mx-auto pt-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-4"
        >
          ← Back to Dashboard
        </Button>

        {/* CSV Upload Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <Upload className="h-6 w-6" />
              Upload OpenSkiData CSV Files
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-900">
                <strong>How it works:</strong> Download the latest CSV files from OpenSkiData, then upload ski_areas.csv, runs.csv, and lifts.csv. These files will be imported into the staging tables and you can then publish resorts to make them available in the app.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-blue-50 transition">
                <label className="cursor-pointer block">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <div className="text-sm font-medium text-slate-900 mb-1">Ski Areas</div>
                  <div className="text-xs text-slate-600 mb-3">ski_areas.csv</div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleCSVUpload(e, 'ski_areas')}
                    disabled={uploading}
                    hidden
                  />
                  <Button size="sm" variant="outline" disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Choose File'}
                  </Button>
                </label>
              </div>

              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-green-50 transition">
                <label className="cursor-pointer block">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <div className="text-sm font-medium text-slate-900 mb-1">Runs</div>
                  <div className="text-xs text-slate-600 mb-3">runs.csv</div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleCSVUpload(e, 'runs')}
                    disabled={uploading}
                    hidden
                  />
                  <Button size="sm" variant="outline" disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Choose File'}
                  </Button>
                </label>
              </div>

              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-purple-50 transition">
                <label className="cursor-pointer block">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <div className="text-sm font-medium text-slate-900 mb-1">Lifts</div>
                  <div className="text-xs text-slate-600 mb-3">lifts.csv</div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleCSVUpload(e, 'lifts')}
                    disabled={uploading}
                    hidden
                  />
                  <Button size="sm" variant="outline" disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Choose File'}
                  </Button>
                </label>
              </div>
            </div>

            {uploadProgress && (
              <div className="text-sm text-slate-600 text-center font-medium">
                {uploadProgress}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Browse & Publish Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <Mountain className="h-6 w-6" />
              {currentStep === 'select' ? 'Browse & Publish Resorts' : 'Publishing Resorts...'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentStep === 'select' && (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <Search className="h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search resorts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1"
                  />
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : filteredResorts.length === 0 ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No resorts found. Upload OpenSkiData CSV files to get started.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-medium text-slate-900">
                        {filteredResorts.length} resorts found • {selectedResorts.size} selected
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={selectAll}>
                          Select All
                        </Button>
                        <Button variant="outline" size="sm" onClick={deselectAll}>
                          Clear
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {filteredResorts.map(resort => (
                        <div
                          key={resort.id}
                          className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition"
                          onClick={() => toggleResort(resort.id)}
                        >
                          <Checkbox
                            checked={selectedResorts.has(resort.id)}
                            onCheckedChange={() => toggleResort(resort.id)}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">{resort.name}</div>
                            <div className="text-sm text-slate-600">
                              {resort.countries || 'Unknown'} {resort.regions ? `• ${resort.regions}` : ''}
                            </div>
                            {resort.linked_ski_area_id && (
                              <div className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded w-fit mt-1">
                                Already published
                              </div>
                            )}
                          </div>
                          {resort.vertical_m && (
                            <div className="text-right text-sm">
                              <div className="text-slate-600">{resort.vertical_m}m vertical</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={publishSelectedResorts}
                      disabled={selectedResorts.size === 0}
                      className="w-full"
                      size="lg"
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Publish {selectedResorts.size > 0 ? `${selectedResorts.size} Resort${selectedResorts.size !== 1 ? 's' : ''}` : 'Resorts'}
                    </Button>
                  </>
                )}
              </>
            )}

            {currentStep === 'publish' && (
              <div className="space-y-2">
                {publishProgress.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                    {item.status === 'publishing' && (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    )}
                    {item.status === 'success' && (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    )}
                    {item.status === 'error' && (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{item.name}</div>
                      {item.error && (
                        <div className="text-xs text-red-600">{item.error}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
